import React, { useMemo, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from './api/http';
import { useLocation } from 'react-router-dom';
import { Button, Input } from 'antd';
import {
  DashboardPageShell,
  DashboardPanel,
  DashboardPrimaryActionButton,
} from './components/dashboard';
import './styles/authOnboarding.css';

const CURRENT_TERMS_VERSION = '2026-08-13-v1.1';

function safeSubToLocalEmail(s) {
  if (!s) return '';
  const cleaned = String(s).replace(/[^a-zA-Z0-9]+/g, '_');
  const short = cleaned.length > 180 ? cleaned.slice(0, 180) : cleaned;
  return `auth0_${short}@auth0.local`;
}

export default function SignupPage() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const subParam = query.get('sub');

  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();

  const [customerName, setCustomerName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('pendingCustomerName');
      if (saved) setCustomerName(saved);
      setAddress(sessionStorage.getItem('pendingCustomerAddress') || '');
      setCity(sessionStorage.getItem('pendingCustomerCity') || '');
      setState(sessionStorage.getItem('pendingCustomerState') || '');
    } catch {}
  }, []);

  function startAuth0Signup(returnTo) {
    setMsg('');
    setErrMsg('');
    try {
      sessionStorage.setItem('pendingCustomerName', (customerName || '').trim());
      sessionStorage.setItem('pendingCustomerAddress', (address || '').trim());
      sessionStorage.setItem('pendingCustomerCity', (city || '').trim());
      sessionStorage.setItem('pendingCustomerState', (state || '').trim());
    } catch {}

    const rt = returnTo || '/signup';
    loginWithRedirect({
      appState: { returnTo: rt },
      authorizationParams: {
        screen_hint: 'signup',
        redirect_uri: `${window.location.origin}${rt}`,
      },
    });
  }

  function startAuth0Login(returnTo) {
    setMsg('');
    setErrMsg('');
    try {
      sessionStorage.setItem('pendingCustomerName', (customerName || '').trim());
      sessionStorage.setItem('pendingCustomerAddress', (address || '').trim());
      sessionStorage.setItem('pendingCustomerCity', (city || '').trim());
      sessionStorage.setItem('pendingCustomerState', (state || '').trim());
    } catch {}

    const rt = returnTo || '/signup';
    loginWithRedirect({
      appState: { returnTo: rt },
      authorizationParams: {
        redirect_uri: `${window.location.origin}${rt}`,
      },
    });
  }

  async function finishCustomerSetup() {
    setMsg('');
    setErrMsg('');

    if (!isAuthenticated) {
      setErrMsg('Create your account first, then come back to finish setup.');
      return;
    }

    const cn = (customerName || '').trim();
    const normalizedAddress = address.trim();
    const normalizedCity = city.trim();
    const normalizedState = state.trim();
    if (!cn || !normalizedAddress || !normalizedCity || !normalizedState) {
      setErrMsg('Company name, facility address, city, and state are required.');
      return;
    }
    if (!termsAccepted) {
      setErrMsg('You must agree to the Customer Agreement before finishing setup.');
      return;
    }

    const sub = subParam || user?.sub || '';
    const fallbackEmail = safeSubToLocalEmail(sub);

    const userEmail = ((user?.email || '').trim() || fallbackEmail).trim();
    const userNameRaw = (user?.name || user?.nickname || '').trim();
    const enteredFirstName = (firstName || '').trim();
    const enteredLastName = (lastName || '').trim();

    if (!enteredFirstName || !enteredLastName) {
      setErrMsg('First name and last name are required.');
      return;
    }

    const userName = `${enteredFirstName} ${enteredLastName}`.trim();
    const displayName = userName || userNameRaw || (userEmail ? userEmail.split('@')[0] : '') || 'customer_admin';

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      localStorage.setItem('accessToken', token);

      await api.post(
        '/api/auth/customer/setup',
        {
          customerName: cn,
          address: normalizedAddress,
          city: normalizedCity,
          state: normalizedState,
          userName: displayName,
          userEmail,
          firstName: enteredFirstName,
          lastName: enteredLastName,
          displayName,
          termsAccepted: true,
          termsVersion: CURRENT_TERMS_VERSION,
        },
        { skipCustomer: true }
      );

      try {
        sessionStorage.removeItem('pendingCustomerName');
        sessionStorage.removeItem('pendingCustomerAddress');
        sessionStorage.removeItem('pendingCustomerCity');
        sessionStorage.removeItem('pendingCustomerState');
      } catch {}

      setMsg('Customer setup complete. Redirecting...');
      window.location.assign('/');
    } catch (e) {
      const data = e?.data ?? e?.response?.data ?? null;
      const message = data?.message || e?.message || 'Customer setup failed.';
      setErrMsg(message);
    } finally {
      setLoading(false);
    }
  }

  function cancel() {
    window.location.replace('/');
  }

  return (
    <DashboardPageShell
      title="Sign up"
      productDashboardShell="customer"
      headerClassName="dashboard-fleet-header"
      contentClassName="dashboard-fleet-page auth-onboarding-page auth-signup-page"
      contentInnerClassName="auth-onboarding-shell"
      shellClassName="auth-onboarding-page-shell"
    >
      <DashboardPanel className="dashboard-fleet-dashboard-panel auth-onboarding-panel">
        <header className="auth-onboarding-header">
          <p className="auth-onboarding-eyebrow">Customer account setup</p>
          <h1 className="auth-onboarding-title">Create your ManageLift company account</h1>
          <p className="auth-onboarding-subtitle">
            Set up a customer administrator login and company workspace. Technician and operator access stays invite-only.
          </p>
        </header>

        <div className="auth-onboarding-content auth-onboarding-content-wide">
          {msg ? <div className="auth-onboarding-alert auth-onboarding-alert-success">{msg}</div> : null}
          {errMsg ? <div className="auth-onboarding-alert auth-onboarding-alert-error">{errMsg}</div> : null}

          <div className="auth-onboarding-section auth-onboarding-form">
            {!isAuthenticated ? (
              <>
                <p className="auth-onboarding-helper">
                  Start by creating or signing into your secure login. When you return, finish the company details below.
                </p>

                <DashboardPrimaryActionButton
                  size="large"
                  className="auth-onboarding-primary"
                  onClick={() => startAuth0Signup('/signup')}
                  disabled={loading}
                >
                  Create account
                </DashboardPrimaryActionButton>

                <Button
                  htmlType="button"
                  size="large"
                  className="dashboard-secondary-action-button auth-onboarding-secondary"
                  onClick={() => startAuth0Login('/signup')}
                  disabled={loading}
                >
                  I already have an account
                </Button>
              </>
            ) : (
              <p className="auth-onboarding-helper">
                Signed in{user?.email ? ` as ${user.email}` : ''}. Finish the company setup below.
              </p>
            )}

            <label className="auth-onboarding-label" htmlFor="customerName">
              Company name
            </label>
            <Input
              id="customerName"
              className="auth-onboarding-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Company name"
              disabled={loading}
            />

            <label className="auth-onboarding-label" htmlFor="address">Facility / operating street address</label>
            <Input id="address" className="auth-onboarding-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" disabled={loading} />

            <label className="auth-onboarding-label" htmlFor="city">City</label>
            <Input id="city" className="auth-onboarding-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" disabled={loading} />

            <label className="auth-onboarding-label" htmlFor="state">State</label>
            <Input id="state" className="auth-onboarding-input" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" disabled={loading} />

            <label className="auth-onboarding-label" htmlFor="firstName">
              Your first name
            </label>
            <Input
              id="firstName"
              className="auth-onboarding-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              disabled={loading}
            />

            <label className="auth-onboarding-label" htmlFor="lastName">
              Your last name
            </label>
            <Input
              id="lastName"
              className="auth-onboarding-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              disabled={loading}
            />

            <DashboardPrimaryActionButton
              size="large"
              className="auth-onboarding-primary"
              onClick={finishCustomerSetup}
              disabled={loading || !isAuthenticated}
              loading={loading}
            >
              Finish setup
            </DashboardPrimaryActionButton>

            <div className="auth-onboarding-terms">
              <input id="termsAccepted" type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} disabled={loading}/>
              <label htmlFor="termsAccepted">I agree to the ManageLift <a href="/terms" target="_blank" rel="noreferrer">Customer Agreement</a> and acknowledge the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Notice</a>.</label>
            </div>

            <Button
              htmlType="button"
              size="large"
              className="dashboard-secondary-action-button auth-onboarding-secondary"
              onClick={cancel}
              disabled={loading}
            >
              Cancel
            </Button>

            <p className="auth-onboarding-note">
              Customer accounts manage the company workspace. Technicians and operators should use their invite links instead.
            </p>
          </div>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );
}
